import { sortBy } from "@repo/utils";
import {
  ALL_TASKS_BRAND_PREFIX,
  ALL_TASKS_PREFIX,
} from "~/pages/dashboard/task/tab-windows/utils/window-helpers";
import type { PinnedTabWindowsState, TabWindowState } from "~/pages/dashboard/task/task.types";

const STORAGE_KEY = "pinned-tab-windows";

export const tabWindowStorage = {
  save(state: PinnedTabWindowsState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error("Failed to save pinned tab windows state:", error);
    }
  },

  load(): PinnedTabWindowsState {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as PinnedTabWindowsState;
        if (parsed.windows && Array.isArray(parsed.windows)) {
          return parsed;
        }
      }
    } catch (error) {
      console.error("Failed to load pinned tab windows state:", error);
    }

    return {
      windows: [],
      nextOrder: 0,
    };
  },

  updateWindow(windowId: string, updates: Partial<TabWindowState>): void {
    const state = this.load();
    const windowIndex = state.windows.findIndex((w) => w.id === windowId);

    if (windowIndex >= 0) {
      const currentWindow = state.windows[windowIndex];
      if (currentWindow) {
        state.windows[windowIndex] = { ...currentWindow, ...updates };
        this.save(state);
      }
    }
  },

  addPinnedWindow(windowState: TabWindowState): void {
    const state = this.load();
    state.windows = state.windows.filter((w) => w.id !== windowState.id);
    state.windows.push(windowState);
    state.nextOrder = Math.max(state.nextOrder, windowState.order + 1);

    this.save(state);
  },

  removePinnedWindow(windowId: string): void {
    const state = this.load();
    state.windows = state.windows.filter((w) => w.id !== windowId);
    this.save(state);
  },

  //TODO: Remove this migration logic after a few releases, once we are confident that all users have migrated to the new window IDs.
  migrateOldWindowIds(): void {
    const state = this.load();
    let changed = false;
    state.windows = state.windows.map((w) => {
      const legacyBrand = w.id.match(/^window-brand-(\d+)-all-tasks$/);
      if (legacyBrand) {
        changed = true;
        const brandId = Number(legacyBrand[1]);
        const newId = brandId > 0 ? `${ALL_TASKS_BRAND_PREFIX}${brandId}` : ALL_TASKS_PREFIX;
        return { ...w, id: newId };
      }
      return w;
    });

    // Drop duplicates a rename may have produced (keep first occurrence).
    const seen = new Set<string>();
    const deduped = state.windows.filter((w) => {
      if (seen.has(w.id)) {
        changed = true;
        return false;
      }
      seen.add(w.id);
      return true;
    });
    state.windows = deduped;
    if (changed) this.save(state);
  },

  getPinnedWindows(): TabWindowState[] {
    this.migrateOldWindowIds();
    const state = this.load();
    return sortBy(
      state.windows.filter((w) => w.isPinned),
      "order",
      "asc"
    );
  },

  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear pinned tab windows state:", error);
    }
  },
};
